import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, theme as antdTheme } from 'antd';

import {
  getPipelineBucket,
  PIPELINE_STAGES,
  PipelineBucketKey,
  totalOrAnnual,
} from '@totem/components/services/optimize/v3/pipeline';
import TicketingContext from '@totem/components/ticketing/listContainer/ticketingContainerContext';
import {
  calculateAnnualSavings,
  isVisibleOpportunity,
} from '@totem/components/ticketing/ticket/specialCases/utilities';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';

const formatCompact = (val: number): string => {
  if (val >= 1_000_000) {
    const mVal = val / 1_000_000;
    return `$${mVal % 1 === 0 ? Math.round(mVal) : mVal.toFixed(1)}M`;
  }
  if (val >= 1000) {
    const kVal = val / 1000;
    return `$${kVal % 1 === 0 ? Math.round(kVal) : kVal.toFixed(1)}K`;
  }
  return `$${Math.round(val).toLocaleString('en-US')}`;
};

const formatFull = (val: number): string =>
  `$${Math.round(val).toLocaleString('en-US')}`;

type StageBucket = {
  tickets: TicketQueryResult[];
  annual: number;
};

// ---------------------------------------------------------------------------
// State strip — a compact row of all six states and their live counts.
// ---------------------------------------------------------------------------

const StateStrip = ({
  buckets,
}: {
  buckets: Record<PipelineBucketKey, StageBucket>;
}) => {
  const { useToken } = antdTheme;
  const { token } = useToken();

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 8,
        marginBottom: 20,
      }}
    >
      {PIPELINE_STAGES.map((stage) => {
        const bucket = buckets[stage.key];
        const live = bucket.tickets.length > 0;
        return (
          <div
            key={stage.key}
            style={{
              border: `1px solid ${live ? '#E8540A' : token.colorBorderSecondary}`,
              borderRadius: 8,
              padding: '10px 12px',
              background: live ? '#FEF0E6' : token.colorBgContainer,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                color: live ? '#9A3C1A' : token.colorTextTertiary,
              }}
            >
              {stage.number}
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                lineHeight: 1.2,
                color: live ? '#9A3C1A' : token.colorText,
              }}
            >
              {bucket.tickets.length}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: token.colorTextSecondary,
              }}
            >
              {stage.name}
            </div>
            <div style={{ fontSize: 11, color: token.colorTextTertiary }}>
              {bucket.annual > 0 ? `${formatCompact(bucket.annual)}/yr` : '—'}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// One card per state — what it means, who owns it, and what sits in it.
// ---------------------------------------------------------------------------

const StateCard = ({
  stageIndex,
  bucket,
}: {
  stageIndex: number;
  bucket: StageBucket;
}) => {
  const { useToken } = antdTheme;
  const { token } = useToken();
  const stage = PIPELINE_STAGES[stageIndex];
  const live = bucket.tickets.length > 0;
  const shown = bucket.tickets.slice(0, 3);
  const remaining = bucket.tickets.length - shown.length;

  return (
    <Card
      size="small"
      style={{ borderColor: live ? '#E8540A' : undefined, height: '100%' }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: token.colorTextTertiary,
          textTransform: 'uppercase',
        }}
      >
        {stage.number} · {stage.name}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, margin: '4px 0 8px' }}>
        {stage.label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: token.colorTextSecondary,
          lineHeight: 1.5,
          marginBottom: 14,
        }}
      >
        {stage.description}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 8,
          paddingTop: 12,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          marginBottom: shown.length > 0 ? 12 : 0,
        }}
      >
        <div>
          <div style={{ fontSize: 10, color: token.colorTextTertiary }}>
            In state
          </div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {bucket.tickets.length}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: token.colorTextTertiary }}>
            Annual
          </div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {bucket.annual > 0 ? formatCompact(bucket.annual) : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: token.colorTextTertiary }}>
            Owner
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>{stage.owner}</div>
        </div>
      </div>
      {shown.length > 0 && (
        <div style={{ fontSize: 12.5, lineHeight: 1.8 }}>
          {shown.map((rec) => (
            <div key={rec.ticket.id}>
              <Link to={`/dashboard/optimize/opportunity/${rec.ticket.id}`}>
                <strong>{rec.ticket.number}</strong>
              </Link>{' '}
              {rec.ticket.summary}
            </div>
          ))}
          {remaining > 0 && (
            <div style={{ color: token.colorTextTertiary }}>
              + {remaining} more
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Decision sheets — what happens after each possible action, shown once
// rather than repeated per opportunity. "If you defer" names the real cost
// of delay across whatever is currently awaiting review.
// ---------------------------------------------------------------------------

const DecisionSheets = ({
  pendingReviewAnnual,
}: {
  pendingReviewAnnual: number;
}) => {
  const { useToken } = antdTheme;
  const { token } = useToken();
  const perMonth = pendingReviewAnnual / 12;

  const sheetStyle: React.CSSProperties = {
    height: '100%',
  };

  const listStyle: React.CSSProperties = {
    fontSize: 13,
    color: token.colorTextSecondary,
    lineHeight: 1.7,
    paddingLeft: 18,
    margin: 0,
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 16,
      }}
    >
      <Card size="small" style={sheetStyle}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#16A34A' }}>
          IF YOU APPROVE
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, margin: '4px 0 10px' }}>
          One confirmation, no other clicks.
        </div>
        <ul style={listStyle}>
          <li>Routes to the assigned IB engineer and your work-order system</li>
          <li>A maintenance window is proposed from the project start date</li>
          <li>
            You receive milestone notices at start, finish, and verification
          </li>
          <li>Savings begin accruing from the day of execution</li>
        </ul>
      </Card>
      <Card size="small" style={sheetStyle}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#D48806' }}>
          IF YOU DEFER
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, margin: '4px 0 10px' }}>
          Re-surfaces in 30 days.
        </div>
        <ul style={listStyle}>
          <li>
            {perMonth > 0 ? (
              <>
                Cost of delay across the current review queue:{' '}
                <strong>{formatFull(perMonth)}/month</strong> in unrealised
                savings
              </>
            ) : (
              'Nothing currently awaiting review has a savings value recorded'
            )}
          </li>
          <li>
            The opportunity stays open on the dashboard and in this report
          </li>
          <li>IB revisits it before the next quarterly review</li>
        </ul>
      </Card>
      <Card size="small" style={sheetStyle}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#CF1322' }}>
          IF YOU REJECT
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, margin: '4px 0 10px' }}>
          Closed with a reason.
        </div>
        <ul style={listStyle}>
          <li>IB removes it from the active queue</li>
          <li>The reason is captured and reported at portfolio level</li>
          <li>It can be re-opened by you or by IB at any time</li>
        </ul>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const OptimizeLifecycleTab = () => {
  const { ticketData } = useContext(TicketingContext);
  const { useToken } = antdTheme;
  const { token } = useToken();

  const allTickets = useMemo(
    () => (ticketData?.tickets ?? []).filter(isVisibleOpportunity),
    [ticketData],
  );

  const buckets = useMemo(() => {
    const acc: Record<PipelineBucketKey, StageBucket> = {
      pendingReview: { tickets: [], annual: 0 },
      approved: { tickets: [], annual: 0 },
      inProgress: { tickets: [], annual: 0 },
      verifying: { tickets: [], annual: 0 },
      confirm: { tickets: [], annual: 0 },
      booked: { tickets: [], annual: 0 },
    };

    allTickets.forEach((rec) => {
      const bucketKey = getPipelineBucket(rec);
      if (!bucketKey) return;
      const opt = rec.ticket.extras?.optimize;
      acc[bucketKey].tickets.push(rec);

      acc[bucketKey].annual += opt ? calculateAnnualSavings(opt) : 0;
    });

    (Object.keys(acc) as PipelineBucketKey[]).forEach((key) => {
      acc[key].tickets.sort(
        (recA, recB) => totalOrAnnual(recB) - totalOrAnnual(recA),
      );
    });

    return acc;
  }, [allTickets]);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: token.colorText,
            marginBottom: 6,
            lineHeight: 1.2,
          }}
        >
          One opportunity, six states.
        </div>
        <div
          style={{
            fontSize: 14,
            color: token.colorTextSecondary,
            lineHeight: 1.6,
            maxWidth: '68ch',
          }}
        >
          The same opportunity changes shape as the work moves — what to do, who
          is doing it, and what you are getting. Counts below are live for this
          scope.
        </div>
      </div>

      <StateStrip buckets={buckets} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16,
          marginBottom: 26,
        }}
      >
        {PIPELINE_STAGES.map((stage, idx) => (
          <StateCard
            key={stage.key}
            stageIndex={idx}
            bucket={buckets[stage.key]}
          />
        ))}
      </div>

      <div style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
        Decision sheets — what comes after each click
      </div>
      <DecisionSheets pendingReviewAnnual={buckets.pendingReview.annual} />
    </div>
  );
};

export default OptimizeLifecycleTab;
